<template>
  <PhaseSection
    data-testid="problem-solving-review"
    data-tourid="problem-solving-review-phase"
    noColumns
    :hasNextButton="hasNextButton"
    :hasBackButton="hasBackButton"
    @next="$emit('next')"
    @back="$emit('back')"
  >
    <template>
      <TranslationLoading v-if="isTranslating" />
      <template v-else>
        <ReviewHeader
          :validationRequestErrorText="validationRequestErrorText"
          :rev="rev"
          :showEstimated="showEstimated"
          :isImpactValidated="isImpactValidated"
          :isImplemented="isImplemented"
          :hasRequestValidation="hasRequestValidation"
          :showRequestError="showRequestError"
          :loading="isSaving"
          :canImplement="canImplement"
          :canDoFinancialValidation="canDoFinancialValidation"
          :isCompleted="isCompleted"
          @showEstimated="handleShowEstimated"
          @implementRev="$emit('implementRev')"
          @handleRequestValidationClick="handleRequestValidationClick"
          @cancelValidationRequest="$emit('cancelValidationRequest', $event)"
          @validate="$emit('validate')"
          @closeRequestError="showRequestError = false"
        />

        <ReviewValidation
          class="mb-2"
          :showValidated="!showEstimated"
          :rev="rev"
          :isImpactValidated="isImpactValidated"
          :hasRequestValidation="hasRequestValidation"
          :isRevImplemented="isImplemented"
          :editMode="isEditMode"
          @update="updateReviewField"
        />

        <RequestValidationModal
          :show="showRequestModal"
          @hideModal="showRequestModal = false"
          @createValidationRequest="$emit('createValidationRequest', $event)"
        />
        <RequestViewModal
          :show="showRequestViewModal"
          :requestValidation="requestValidationInProgress"
          @hideModal="showRequestViewModal = false"
          @cancelValidationRequest="$emit('cancelValidationRequest', $event)"
        />
      </template>
    </template>
  </PhaseSection>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin'
import currencies from '@/locales/currencies.json'

import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'

import costReductionImage from '@/assets/rev/cost_reduction.svg'
import costReductionIcon from '@/assets/rev/icon_cost_reduction.svg'
import investmentImage from '@/assets/rev/investment.svg'
import investmentIcon from '@/assets/rev/icon_investment.svg'
import revenueImage from '@/assets/rev/revenue.svg'
import revenueIcon from '@/assets/rev/icon_revenue.svg'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'
import RequestValidationModal from '@/containers/Modals/RequestValidationModal.vue'
import RequestViewModal from '@/containers/Modals/RequestViewModal.vue'
import ReviewValidation from '@/components/Rev/Review/ReviewValidation.vue'
import ReviewHeader from '@/components/Rev/Review/ReviewHeader.vue'

const FILTERS = {
  ESTIMATED: 'Estimated',
  VALIDATED: 'Validated',
}
export default {
  name: 'ProblemSolvingReviewStep',

  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    rev: {
      type: Object,
      required: true,
    },
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    hasRequestValidation: Boolean,
    canImplement: Boolean,
    isSaving: Boolean,
    canDoFinancialValidation: Boolean,
  },

  components: {
    PhaseSection,
    TranslationLoading,
    RequestValidationModal,
    RequestViewModal,
    ReviewValidation,
    ReviewHeader,
  },

  mixins: [trackerMixin, mixin],

  data() {
    return {
      costReductionImage,
      costReductionIcon,
      investmentImage,
      investmentIcon,
      revenueImage,
      revenueIcon,
      showRequestModal: false,
      showRequestViewModal: false,
      FILTERS,
      selectedFilter: FILTERS.ESTIMATED,
      showEstimated: true,
      showRequestError: false,
      validationRequestErrorText: '',
    }
  },
  mounted() {
    this.selectedFilter = this.showFilters
      ? FILTERS.VALIDATED
      : FILTERS.ESTIMATED
    this.showEstimated = !this.isImpactValidated
  },
  computed: {
    showFilters() {
      return this.isImpactValidated
    },

    validatedFilterIsSelected() {
      return Boolean(this.selectedFilter === FILTERS.VALIDATED)
    },

    validatedIcon() {
      return this.validatedFilterIsSelected
        ? 'mdi-check-decagram'
        : 'mdi-check-decagram-outline'
    },

    isImpactValidated() {
      return Boolean(this.rev?.hasFinancialValidation)
    },

    selectedCategories: {
      get() {
        return this.rev?.categories ?? []
      },

      set(categories) {
        this.updateReviewField('categories', categories)
      },
    },

    currency() {
      return this.rev?.currencyMetadata.currentCurrency
    },

    organizationCurrency() {
      return this.rev?.currencyMetadata?.globalCurrency
    },

    exchangeRate() {
      const exchangeRate = this.rev?.currencyMetadata?.exchangeRate || '0'
      return Number(exchangeRate)
    },

    currencySymbol() {
      const currencyData = currencies[this.currency]
      return currencyData.symbol_native
    },

    organizationCurrencySymbol() {
      const currencyData = currencies[this.organizationCurrency]
      return currencyData.symbol_native
    },

    costReduction() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.costReduction?.capturedValue
        : this.rev?.reviewPhase?.costReduction?.capturedValue
      const parsedValue = Number(value)

      return _.isNaN(parsedValue) ? null : parsedValue
    },

    investment() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.investment?.capturedValue
        : this.rev?.reviewPhase?.investment?.capturedValue
      const parsedValue = Number(value)

      return _.isNaN(parsedValue) ? null : parsedValue
    },

    revenue() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.revenue?.capturedValue
        : this.rev?.reviewPhase?.revenue?.capturedValue
      const parsedValue = Number(value)

      return _.isNaN(parsedValue) ? null : parsedValue
    },

    timeInvested() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.timeTracking?.timeInvested
        : this.rev?.reviewPhase?.timeTracking?.timeInvested

      if (!_.isString(value) && !_.isNumber(value)) return null
      return Number(value)
    },

    timeReductionByMonth() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview.timeTracking.timeReductionByMonth
        : this.rev?.reviewPhase?.timeTracking?.timeReductionByMonth

      if (!_.isString(value) && !_.isNumber(value)) return null
      return Number(value)
    },

    estimationExplanation() {
      if (this.validatedFilterIsSelected)
        return this.rev?.validatedReview?.estimationExplanation
      return this.rev?.reviewPhase?.estimationExplanation
    },

    siteId() {
      return this.rev.location?.site?._id || this.rev.location?.site
    },

    isImplemented() {
      return Boolean(this.rev?.hasImplementationValidation)
    },

    requestValidationInProgress() {
      return this.rev?.validationRequest?.find(
        validationRequest =>
          validationRequest?.status ===
          this.$constants.validationRequestStatus.inProgress
      )
    },

    isCompleted() {
      return this.rev.status === this.$constants.revStatus.completed
    },
  },

  methods: {
    ...mapMutations({
      updateCostReduction: 'problemSolving/updateCostReduction',
      updateInvestment: 'problemSolving/updateInvestment',
      updateEstimationExplanation: 'problemSolving/updateEstimationExplanation',
      updateCategories: 'problemSolving/updateCategories',
      updateRevenue: 'problemSolving/updateRevenue',
      updateTimeInvested: 'problemSolving/updateTimeInvested',
      updateTimeReductionByMonth: 'problemSolving/updateTimeReductionByMonth',
      updateImpactMetrics: 'problemSolving/updateImpactMetrics',
      updateInvestments: 'problemSolving/updateInvestments',
      updateStartDate: 'problemSolving/updateStartDate',
      updateEndDate: 'problemSolving/updateEndDate',
      setImplementError: 'setImplementError',
    }),

    handleRequestValidationClick() {
      if (!this.isCompleted) {
        this.validationRequestErrorText = this.$t(
          'rev.validation.mark_as_completed_error'
        )
        this.showRequestError = true
        return
      }

      if (!this.isImplemented) {
        this.validationRequestErrorText = this.$t(
          'common.button.mark_this_rev_as_implemented_to_request_validation'
        )
        this.showRequestError = true
        this.setImplementError(true)
        return
      }
      this.trackValidationRequest({
        action: this.hasRequestValidation ? 'view' : 'request',
        type: 'buttonClick',
      })
      if (this.hasRequestValidation) {
        this.showRequestViewModal = true
        return
      }

      this.showRequestModal = true
    },

    shouldShowMoneyQuestion(value) {
      return this.isEditMode || _.isNil(value)
    },

    updateReviewField(field, value) {
      this.$emit('revChange')

      switch (field) {
        case 'costReduction':
          return this.updateCostReduction(value)
        case 'investment':
          return this.updateInvestment(value)
        case 'estimationExplanation':
          return this.updateEstimationExplanation(value)
        case 'categories':
          this.trackUpdate(this.rev, 'changeReviewCategories')
          return this.updateCategories(value)
        case 'revenue':
          return this.updateRevenue(value)
        case 'timeInvested':
          return this.updateTimeInvested(value)
        case 'timeReductionByMonth':
          return this.updateTimeReductionByMonth(value)
        case 'impactMetrics':
          return this.updateImpactMetrics(value)
        case 'investments':
          return this.updateInvestments(value)
        case 'startDate':
          return this.updateStartDate(value)
        case 'endDate':
          return this.updateEndDate(value)
      }
    },

    calculateRate(value) {
      return Math.round(parseInt(value) / this.exchangeRate)
    },

    trackEvent: function (changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    selectFilter(filter) {
      this.trackClickReviewValidationFilter({ type: filter })
      this.selectedFilter = filter
    },

    handleShowEstimated() {
      this.showEstimated = !this.showEstimated
    },
  },

  watch: {
    isCompleted(newVal) {
      if (newVal) {
        this.showRequestError = false
      }
    },
    isImplemented(newVal) {
      if (newVal) {
        this.showRequestError = false
      }
    },
  },
}
</script>
